import { SkipLink } from '@today/ui-components'
import { cn, Logo, spinnerVariants } from '@today/ui-primitives'
import { type ReactElement, type ReactNode, useId } from 'react'

/** The status tint of an {@link AuthShellView} card. */
export enum AuthShellTone {
	Neutral = 'neutral',
	Error = 'error',
	Warning = 'warning',
}

/** The brand logo an auth card shows (`alt` is the brand name). */
export interface AuthLogo {
	src: string
	alt: string
}

/**
 * Props for {@link AuthShellView}.
 *
 * @property logo - The bound brand's logo; omitted, no image renders.
 * @property title - The screen's single level-1 heading.
 * @property subtitle - Optional line under the heading.
 * @property tone - `error`/`warning` add a tinted icon badge above the heading.
 * @property children - The card body.
 * @property footer - Optional content under the body.
 */
export interface AuthShellViewProps {
	logo?: AuthLogo
	title: string
	subtitle?: string
	tone?: AuthShellTone
	children?: ReactNode
	footer?: ReactNode
}

const BADGE: Record<Exclude<AuthShellTone, AuthShellTone.Neutral>, { className: string; path: string }> = {
	[AuthShellTone.Error]: {
		className: 'bg-ds-danger/15 text-ds-danger',
		path: 'M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z',
	},
	[AuthShellTone.Warning]: {
		className: 'bg-ds-warning/15 text-ds-warning',
		path: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
	},
}

/**
 * The sign-in card (TO-361 login-surface REQ-009): a centred card on the muted
 * page surface with the brand logo, one h1, an optional subtitle, a body and a
 * footer. Full width inside a `ds-4` gutter below 640 px, capped at 28rem above.
 * Pure — the page supplies the logo and every string.
 */
export function AuthShellView({
	logo,
	title,
	subtitle,
	tone = AuthShellTone.Neutral,
	children,
	footer,
}: AuthShellViewProps): ReactElement {
	const badge = tone === AuthShellTone.Neutral ? null : BADGE[tone]
	const mainId = useId()
	return (
		<div className="flex min-h-screen items-center justify-center bg-ds-surface-muted px-ds-4 py-ds-8">
			<SkipLink targetId={mainId} />
			<main
				id={mainId}
				data-testid="auth-card"
				className="flex w-full max-w-md flex-col items-center gap-ds-6 rounded-ds-surface bg-ds-surface p-ds-8 text-ds-text shadow-ds-card"
			>
				{logo ? <Logo src={logo.src} alt={logo.alt} size="lg" /> : null}
				{badge ? (
					<div
						data-testid="auth-tone-badge"
						className={cn('flex h-ds-12 w-ds-12 items-center justify-center rounded-ds-full', badge.className)}
					>
						<svg
							className="h-ds-6 w-ds-6"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d={badge.path} />
						</svg>
					</div>
				) : null}
				<header className="flex flex-col items-center gap-ds-2 text-center">
					<h1 className="font-ds-heading text-ds-2xl font-bold text-ds-text">{title}</h1>
					{subtitle ? <p className="text-ds-sm text-ds-text-muted">{subtitle}</p> : null}
				</header>
				{children ? <div className="flex w-full flex-col gap-ds-4">{children}</div> : null}
				{footer ? <footer className="w-full text-center text-ds-xs text-ds-text-muted">{footer}</footer> : null}
			</main>
		</div>
	)
}

/**
 * A polite `role="status"` line with a decorative spinner, for the sign-in
 * screens' loading states.
 */
export function AuthStatusLine({ text }: { text: string }): ReactElement {
	return (
		<p role="status" className="flex items-center justify-center gap-ds-2 text-ds-sm text-ds-text-muted">
			<span aria-hidden="true" className={spinnerVariants({ size: 'sm' })} />
			{text}
		</p>
	)
}
